import { z } from "zod";

function parseEmailCsv(value: string) {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

const emailArraySchema = z
  .string()
  .transform((value, ctx) => {
    const parsed = parseEmailCsv(value);
    const emailValidator = z.string().email();
    for (const email of parsed) {
      const result = emailValidator.safeParse(email);
      if (!result.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Email invalido en CSV: ${email}`,
        });
      }
    }
    return parsed;
  })
  .pipe(z.array(z.string().email()).min(1));

const envSchema = z.object({
  NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
  APP_BASE_URL: z.url(),
  SELFIA_LEGAL_BUNDLE_VERSION: z.string().min(1).default("2026-03-24-v1"),
  SELFIA_AGE_GATE_ENABLED: z.coerce.boolean().default(false),
  MESSAGING_PROVIDER: z.enum(["twilio", "meta"]).default("meta"),
  WELCOME_GIFT_ENABLED: z.coerce.boolean().default(true),
  GENERATION_PROVIDER: z.enum(["replicate", "fal"]).default("replicate"),
  GENERATION_MODEL: z.enum(["flux-dev", "nano-banana-pro"]).default("flux-dev"),
  BACKEND_API_KEY: z.string().min(1).optional(),
  DATABASE_URL: z.string().min(1),
  DIRECT_URL: z.string().min(1).optional(),
  /** Ruta del objeto en el bucket (bucket privado); preferido frente a URL pública. */
  R2_INSTRUCTIONS_OBJECT_KEY: z.string().min(1).optional(),
  /** URL alcanzable por el servidor (pública o presignada); legado si usás object key. */
  R2_INSTRUCTIONS_IMAGE_URL: z.url().optional(),
  R2_ACCOUNT_ID: z.string().min(1),
  R2_ACCESS_KEY_ID: z.string().min(1),
  R2_SECRET_ACCESS_KEY: z.string().min(1),
  R2_BUCKET_NAME: z.string().min(1).default("selfia"),
  R2_ENDPOINT: z.url(),
  R2_PUBLIC_BASE_URL: z.url().optional(),
  R2_SIGNED_URL_EXPIRES_SECONDS: z.coerce.number().int().positive().default(900),
  STRIPE_SECRET_KEY: z.string().min(1),
  STRIPE_WEBHOOK_SECRET: z.string().min(1),
  STRIPE_PRICE_CURRENCY: z.string().default("usd"),
  BILLING_CURRENCY: z.enum(["usd", "ars"]),
  PRICING_USD_TO_ARS_RATE: z.coerce.number().positive().default(1500),
  PACK_CHECKOUT_TTL_MINUTES: z.coerce.number().int().positive().default(45),
  /** Cantidad de fotos entregadas por round (fuente única para packs). */
  PHOTOS_PER_ROUND: z.coerce.number().int().positive().default(6),
  SUCCESS_REDIRECT_URL: z.url().default("https://wa.me"),
  ERROR_REDIRECT_URL: z.url().default("https://wa.me"),
  I18N_ENABLE_EN_FOR_NON_SPANISH: z.coerce.boolean().default(false),
  DEV_OVERRIDES_ENABLED: z.coerce.boolean().default(false),
  DEV_OVERRIDE_COUNTRY_CODE: z.string().min(1).optional(),
  /** Override de cantidad de outputs SOLO en dev/test (por ejemplo para abaratar pruebas de packs). */
  DEV_PREVIEW_OUTPUT_COUNT: z.coerce.number().int().min(1).optional(),
  DEV_OVERRIDE_PROVIDER: z.enum(["stripe", "mercadopago", "paypal"]).optional(),
  DEV_OVERRIDE_BILLING_CURRENCY: z.enum(["usd", "ars"]).optional(),
  DEV_OVERRIDE_LOCALE: z.enum(["es", "en"]).optional(),
  GROQ_API_KEY: z.string().min(1).optional(),
  GROQ_MODEL: z.string().min(1).default("llama-3.1-8b-instant"),
  GROQ_TIMEOUT_MS: z.coerce.number().int().positive().default(5000),
  GROQ_VISION_MODEL: z.string().min(1).default("llama-3.2-11b-vision-preview"),
  GROQ_VISION_TIMEOUT_MS: z.coerce.number().int().positive().default(6000),
  WELCOME_QUICK_START_ENABLED: z.coerce.boolean(),
  WELCOME_QUICK_START_AI_ENABLED: z.coerce.boolean().default(true),
  WELCOME_QUICK_START_DETERMINISTIC_FALLBACK_ENABLED: z.coerce.boolean().default(true),
  WELCOME_QUICK_START_MIN_CONFIDENCE: z.coerce.number().min(0).max(1).default(0.75),
  WELCOME_DESCRIPTION_REFINER_ENABLED: z.coerce.boolean().default(true),
  DESCRIPTION_REFINER_ENABLED: z.coerce.boolean().default(false),
  DESCRIPTION_REFINER_SCOPE: z.enum(["welcome_only", "all_user_descriptions"]).default("welcome_only"),
  DESCRIPTION_REFINER_MAX_INPUT_CHARS: z.coerce.number().int().positive().default(700),
  DESCRIPTION_REFINER_TIMEOUT_MS: z.coerce.number().int().positive().default(7000),
  DESCRIPTION_REFINER_MAX_RETRIES: z.coerce.number().int().min(0).default(1),
  SUPPORT_EMAIL_ENABLED: z.coerce.boolean().default(true),
  SUPPORT_EMAIL_TO: z.string().email().default("app.selfia@gmail.com"),
  FAL_BILLING_ALERT_EMAILS: emailArraySchema.default([
    "app.selfia@gmail.com",
    "svenandreasclausz@gmail.com",
  ]),
  GENERATION_PENDING_DELIVERY_ALERT_EMAILS: emailArraySchema.default([
    "app.selfia@gmail.com",
    "svenandreasclausz@gmail.com",
  ]),
  SUPPORT_EMAIL_FROM: z.string().email().optional(),
  RESEND_API_KEY: z.string().min(1).optional(),
  /** Si el usuario elige "cargar nuevas" en face_set, purga fotos previas (DB + R2) antes de aceptar nuevas. */
  FACE_SET_PURGE_ON_UPLOAD_NEW_ENABLED: z.coerce.boolean().default(true),
  /** Activa el wizard guiado de 5 pasos para estilos face_set. Default false hasta QA. */
  GUIDED_FACE_UPLOAD_ENABLED: z.coerce.boolean().default(false),
  /** Paso opcional de descripción extra antes de generar. Default false (opt-in en prod). */
  EXTRA_DESCRIPTION_STEP_ENABLED: z.coerce.boolean().default(false),
  /** Claves R2 para las imágenes de guía por paso del wizard (opcionales; fallback a R2_INSTRUCTIONS_OBJECT_KEY). */
  R2_INSTRUCTIONS_FACE_SMILE_FRONT_KEY: z.string().min(1).optional(),
  R2_INSTRUCTIONS_FACE_SERIOUS_FRONT_KEY: z.string().min(1).optional(),
  R2_INSTRUCTIONS_FACE_PROFILE_RIGHT_KEY: z.string().min(1).optional(),
  R2_INSTRUCTIONS_FACE_PROFILE_LEFT_KEY: z.string().min(1).optional(),
  R2_INSTRUCTIONS_FACE_HALF_BODY_KEY: z.string().min(1).optional(),
  TWILIO_ACCOUNT_SID: z.string().optional(),
  TWILIO_AUTH_TOKEN: z.string().optional(),
  TWILIO_WHATSAPP_FROM: z.string().optional(),
  META_WA_API_VERSION: z.string().optional(),
  META_WA_PHONE_NUMBER_ID: z.string().optional(),
  META_WA_ACCESS_TOKEN: z.string().optional(),
  META_WA_APP_SECRET: z.string().optional(),
  META_WA_WEBHOOK_VERIFY_TOKEN: z.string().optional(),
  TWILIO_STATUS_CALLBACK_URL: z.url().optional(),
  TWILIO_INTERACTIVE_ENABLED: z.coerce.boolean().default(true),
  TWILIO_INTERACTIVE_WELCOME_LIST_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_PACK_GRANTED_LIST_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_PHOTO_SOURCE_BUTTONS_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_FEEDBACK_BUTTONS_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_UPLOAD_COLLECTION_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_UPLOAD_INSTRUCTIONS_BACK_ONLY_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_GENERATION_INPUT_REJECTED_SID: z.string().min(1).optional(),
  /** Preview input rejected (422): REGENERAR / CARGAR NUEVAS IMÁGENES. Falls back to GENERATION_INPUT_REJECTED SID if missing. */
  TWILIO_INTERACTIVE_PREVIEW_INPUT_REJECTED_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_TERMS_PRIVACY_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_FEEDBACK_FOLLOWUP_SID: z.string().min(1).optional(),
  /** Prompt opcional de descripción extra con botones VOLVER / OMITIR. */
  TWILIO_INTERACTIVE_EXTRA_DESCRIPTION_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_PACK_OFFER_FIRST_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_PACK_OFFER_REPEAT_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_PACK_CHECKOUT_LINK_STRIPE_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_PACK_CHECKOUT_LINK_MERCADOPAGO_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_GIFT_DELIVERY_FIRST_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_GIFT_DELIVERY_REPEAT_SID: z.string().min(1).optional(),
  /** Guided face upload wizard: new Twilio Content SIDs (partial adoption — §8.3 design). */
  TWILIO_INTERACTIVE_FACE_GUIDE_BACK_ONLY_SID: z.string().min(1).optional(),
  TWILIO_INTERACTIVE_FACE_GUIDE_BACK_CONTINUE_SID: z.string().min(1).optional(),
  /** Guided face upload wizard: quality feedback continue-only (no VOLVER). */
  TWILIO_INTERACTIVE_FACE_GUIDE_CONTINUE_ONLY_SID: z.string().min(1).optional(),
  TWILIO_TYPING_INDICATOR_ENABLED: z.coerce.boolean().default(false),
  TWILIO_TYPING_DURING_GENERATION_ENABLED: z.coerce.boolean().default(true),
  TWILIO_TYPING_HEARTBEAT_MS: z.coerce.number().int().positive().default(22000),
  TWILIO_MEDIA_TIMEOUT_MS: z.coerce.number().int().positive().default(12000),
  MERCADOPAGO_ENABLED: z.coerce.boolean().default(false),
  MERCADOPAGO_ACCESS_TOKEN: z.string().min(1).optional(),
  MERCADOPAGO_PUBLIC_KEY: z.string().min(1).optional(),
  MERCADOPAGO_WEBHOOK_SECRET: z.string().min(1).optional(),
  MERCADOPAGO_API_BASE_URL: z.url().optional(),
  MERCADOPAGO_USE_SANDBOX_CHECKOUT: z.coerce.boolean().default(false),
  /** Opcional: forzar host REST (`https://api-m.sandbox.paypal.com` / `https://api-m.paypal.com`). Si falta, sandbox fuera de prod. */
  PAYPAL_API_BASE_URL: z.url().optional(),
  PAYPAL_CLIENT_ID: z.string().min(1).optional(),
  PAYPAL_CLIENT_SECRET: z.string().min(1).optional(),
  /** ID del webhook registrado en la REST App (mismo ambiente que las credenciales). */
  PAYPAL_WEBHOOK_ID: z.string().min(1).optional(),
  REPLICATE_API_TOKEN: z.string().min(1).optional(),
  REPLICATE_TIMEOUT_MS: z.coerce.number().int().positive().default(25000),
  REPLICATE_WEBHOOK_SECRET: z.string().min(1).optional(),
  REPLICATE_WEBHOOK_TOLERANCE_SECONDS: z.coerce.number().int().positive().default(300),
  /** HMAC del número para `whatsapp_account_lifecycle` y autoborrado; requerido en producción. */
  WHATSAPP_LIFECYCLE_IDENTITY_SECRET: z.string().min(32).optional(),
  FAL_KEY: z.string().min(1).optional(),
  FAL_MODEL: z.string().min(1).default("fal-ai/nano-banana-2/edit"),
  FAL_MODEL_TEXT: z.string().min(1).default("fal-ai/nano-banana-2"),
  FAL_TIMEOUT_MS: z.coerce.number().int().positive().default(25000),
  FAL_WEBHOOK_VERIFY_ENABLED: z.coerce.boolean().default(true),
  FAL_WEBHOOK_TOLERANCE_SECONDS: z.coerce.number().int().positive().default(300),
  FAL_JWKS_URL: z.url().default("https://rest.fal.ai/.well-known/jwks.json"),
  FAL_JWKS_CACHE_TTL_SECONDS: z.coerce.number().int().positive().default(86400),
  FAL_WEBHOOK_ALLOWED_USER_ID: z.string().min(1).optional(),
  FAL_DISPATCH_INTERVAL_MS: z.coerce.number().int().positive().default(1000),
  FAL_MAX_INFLIGHT: z.coerce.number().int().positive().default(1),
  FAL_TICK_MAX_JOBS: z.coerce.number().int().positive().default(1),
  FAL_MAX_ATTEMPTS: z.coerce.number().int().positive().default(8),
  FAL_RETRY_BASE_MS: z.coerce.number().int().positive().default(5000),
  FAL_RETRY_MAX_MS: z.coerce.number().int().positive().default(60000),
  FAL_LOCK_LEASE_MS: z.coerce.number().int().positive().default(30000),
  STAPE_RECEIVER_URL: z.string().url().default("https://news.selfia.app/stape-receiver"),
});

type EnvBase = z.infer<typeof envSchema>;

function requireMetaFields(data: EnvBase, ctx: z.RefinementCtx) {
  const keys = [
    "META_WA_API_VERSION",
    "META_WA_PHONE_NUMBER_ID",
    "META_WA_ACCESS_TOKEN",
    "META_WA_APP_SECRET",
    "META_WA_WEBHOOK_VERIFY_TOKEN",
  ] as const;
  for (const key of keys) {
    const value = data[key];
    if (typeof value !== "string" || !value.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: `Requerido cuando MESSAGING_PROVIDER=meta.`,
      });
    }
  }
}

function requireTwilioCore(data: EnvBase, ctx: z.RefinementCtx) {
  if (!data.TWILIO_ACCOUNT_SID?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["TWILIO_ACCOUNT_SID"],
      message: "Requerido cuando MESSAGING_PROVIDER=twilio.",
    });
  }
  if (!data.TWILIO_AUTH_TOKEN?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["TWILIO_AUTH_TOKEN"],
      message: "Requerido cuando MESSAGING_PROVIDER=twilio.",
    });
  }
  if (!data.TWILIO_WHATSAPP_FROM?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["TWILIO_WHATSAPP_FROM"],
      message: "Requerido cuando MESSAGING_PROVIDER=twilio.",
    });
  }
}

const envSchemaWithProviderRules = envSchema.superRefine((data, ctx) => {
  if (data.MESSAGING_PROVIDER === "twilio") {
    requireTwilioCore(data, ctx);
  }
  if (data.MESSAGING_PROVIDER === "meta") {
    requireMetaFields(data, ctx);
  }
  if (data.NODE_ENV === "production" && !data.WHATSAPP_LIFECYCLE_IDENTITY_SECRET?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["WHATSAPP_LIFECYCLE_IDENTITY_SECRET"],
      message: "Requerido en producción para autoborrado de cuenta y elegibilidad de regalo.",
    });
  }
  if (data.NODE_ENV === "production" && !data.BACKEND_API_KEY?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["BACKEND_API_KEY"],
      message:
        "Requerido en producción para proteger /api/internal y /api/payments (middleware + proxy).",
    });
  }
});

export type Env = z.infer<typeof envSchemaWithProviderRules>;

let cachedEnv: Env | undefined;

export function getEnv(): Env {
  if (cachedEnv) {
    return cachedEnv;
  }

  const parsed = envSchemaWithProviderRules.safeParse(process.env);
  if (!parsed.success) {
    const formatted = parsed.error.issues
      .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
      .join("; ");
    throw new Error(`Variables de entorno invalidas: ${formatted}`);
  }

  cachedEnv = parsed.data;
  return cachedEnv;
}

/** Solo para no-producción si falta env; nunca uses este valor en prod. */
const DEV_WHATSAPP_LIFECYCLE_SECRET_FALLBACK =
  "__selfia_local_dev_whatsapp_lifecycle_secret_32__";

/**
 * Secreto HMAC para `whatsapp_account_lifecycle`. En producción viene siempre de env (validado al arrancar).
 * En development/test, si no está en `.env`, se usa un fallback fijo para que autoborrado y reingreso funcionen en local.
 */
export function resolveWhatsappLifecycleIdentitySecret(): string {
  const env = getEnv();
  const fromEnv = env.WHATSAPP_LIFECYCLE_IDENTITY_SECRET?.trim();
  if (fromEnv) {
    return fromEnv;
  }
  if (env.NODE_ENV !== "production") {
    return DEV_WHATSAPP_LIFECYCLE_SECRET_FALLBACK;
  }
  throw new Error("WHATSAPP_LIFECYCLE_IDENTITY_SECRET requerido en producción.");
}

